/**
 * Article page → "Review link & comments".
 *
 * Mint a private, non-indexed link (`/review/{token}`) to send a story
 * to someone for review BEFORE it goes to WordPress, and read what they
 * wrote back. Mirrors the Sponsor Ads "Share with client" block: the
 * token is stored hashed server-side, so the full URL is shown exactly
 * once, right after it is created — mint a new link if it is lost.
 *
 * Comments from every link on this article are listed oldest first;
 * "Mark addressed" keeps them for the record, the trash icon removes
 * them. Reviewers see a small "addressed" tick on their comment.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Ban, CheckCircle2, Circle, Copy, ExternalLink, Link2, Loader2,
  MessageSquare, Plus, Trash2,
} from "lucide-react";
import { api } from "@/lib/api";
import { tr, uiLocale } from "@/lib/i18n";

const EXPIRY_OPTIONS = [
  { value: "3", get label() { return tr("3 days"); } },
  { value: "7", get label() { return tr("7 days"); } },
  { value: "14", get label() { return tr("14 days"); } },
  { value: "30", get label() { return tr("30 days"); } },
  { value: "", get label() { return tr("Never expires"); } },
];

export function reviewUrlFor(token) {
  return `${window.location.origin}/review/${token}`;
}

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(uiLocale());
}

export default function StoryReviewPanel({ article }) {
  const qc = useQueryClient();
  const articleId = article.id;
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState("14");
  const [fresh, setFresh] = useState(null); // { id, url } — shown once

  const linksQ = useQuery({
    queryKey: ["review-links", articleId],
    queryFn: () => api.listReviewLinks(articleId),
  });
  const commentsQ = useQuery({
    queryKey: ["review-comments", articleId],
    queryFn: () => api.listReviewComments(articleId),
    refetchInterval: 60000,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["review-links", articleId] });
    qc.invalidateQueries({ queryKey: ["review-comments", articleId] });
  };

  const create = useMutation({
    mutationFn: () =>
      api.createReviewLink(articleId, {
        label: label.trim(),
        expires_in_days: expiry ? Number(expiry) : null,
      }),
    onSuccess: (res) => {
      setFresh({ id: res.link.id, url: reviewUrlFor(res.token) });
      setLabel("");
      invalidate();
      toast.success(tr("Review link created — copy it now, it won't be shown again"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Couldn't create link")),
  });

  const revoke = useMutation({
    mutationFn: (linkId) => api.revokeReviewLink(articleId, linkId),
    onSuccess: (_r, linkId) => {
      if (fresh?.id === linkId) setFresh(null);
      invalidate();
      toast.success(tr("Link withdrawn"));
    },
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Couldn't withdraw link")),
  });

  const resolve = useMutation({
    mutationFn: ({ id, resolved }) =>
      api.resolveReviewComment(articleId, id, resolved),
    onSuccess: invalidate,
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Couldn't update comment")),
  });

  const remove = useMutation({
    mutationFn: (id) => api.deleteReviewComment(articleId, id),
    onSuccess: invalidate,
    onError: (e) => toast.error(e?.response?.data?.detail || tr("Couldn't delete comment")),
  });

  const copy = async (url) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(tr("Link copied"));
    } catch {
      window.prompt(tr("Copy this link:"), url);
    }
  };

  const links = linksQ.data || [];
  const active = links.filter((l) => l.status === "active");
  const inactive = links.filter((l) => l.status !== "active");
  const comments = commentsQ.data?.comments || [];

  return (
    <div data-testid="story-review-panel" className="border border-[var(--border)] bg-white">
      {/* ---- Comments ---- */}
      <div className="px-4 py-3 border-b border-[var(--border)]">
        <div className="overline mb-2 inline-flex items-center gap-1.5">
          <MessageSquare className="w-3 h-3" />{" "}{tr("Reviewer comments")}{comments.length > 0 && (
            <span className="normal-case tracking-normal text-[var(--ink-muted)]">{tr("· {open_count} open of {commentCount}", { open_count: commentsQ.data?.open_count || 0, commentCount: comments.length })}
            </span>
          )}
        </div>
        {commentsQ.isLoading ? (
          <div className="text-[11px] text-[var(--ink-muted)] inline-flex items-center gap-1.5">
            <Loader2 className="w-3 h-3 animate-spin" />{" "}{tr("Loading…")}</div>
        ) : comments.length === 0 ? (
          <div className="text-[11px] text-[var(--ink-muted)]">{tr("No comments yet. Create a link below and send it to your reviewer.")}</div>
        ) : (
          <ul className="space-y-2">
            {comments.map((c) => (
              <li
                key={c.id}
                data-testid={`review-comment-${c.id}`}
                className={
                  "border border-[var(--border)] p-3 text-sm " +
                  (c.resolved ? "opacity-60" : "")
                }
              >
                <div className="flex items-center gap-2 text-[11px] font-mono-display text-[var(--ink-muted)] mb-1 flex-wrap">
                  <span className="font-medium text-[var(--ink)]">{c.author_name}</span>
                  <span>{fmt(c.created_at)}</span>
                  {c.link_label && <span>{tr("· via “{link_label}”", { link_label: c.link_label })}</span>}
                  <span className="ml-auto inline-flex items-center gap-2">
                    <button
                      type="button"
                      data-testid={`review-comment-resolve-${c.id}`}
                      onClick={() => resolve.mutate({ id: c.id, resolved: !c.resolved })}
                      className={
                        "inline-flex items-center gap-1 " +
                        (c.resolved
                          ? "text-emerald-700"
                          : "hover:text-emerald-700")
                      }
                      title={c.resolved ? tr("Mark as open again") : tr("Mark addressed")}
                    >
                      {c.resolved ? (
                        <CheckCircle2 className="w-3.5 h-3.5" />
                      ) : (
                        <Circle className="w-3.5 h-3.5" />
                      )}
                      {c.resolved ? tr("Addressed") : tr("Mark addressed")}
                    </button>
                    <button
                      type="button"
                      data-testid={`review-comment-delete-${c.id}`}
                      onClick={() => {
                        if (window.confirm(tr("Delete this comment? This cannot be undone."))) {
                          remove.mutate(c.id);
                        }
                      }}
                      className="hover:text-red-600"
                      title={tr("Delete comment")}
                      aria-label={tr("Delete comment")}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </span>
                </div>
                {c.quote && (
                  <blockquote className="border-l-2 border-[var(--accent)] pl-2 my-1 text-xs italic text-[var(--ink-muted)]">
                    “{c.quote}”
                  </blockquote>
                )}
                <div className="whitespace-pre-wrap leading-relaxed">{c.text}</div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ---- Links ---- */}
      <div className="px-4 py-3 space-y-3">
        <p className="text-[11px] text-[var(--ink-muted)] leading-snug">{tr("A review link opens a read-only copy of this story — headline, body and images — with a comment box. It is not sent to WordPress and is hidden from search engines. Anyone with the link can read it, so send it privately and withdraw it when the review is done. The page always shows the latest saved version of the story.")}</p>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <input
            data-testid="review-link-label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={tr("Label (e.g. sent to Maria for fact check)")}
            maxLength={120}
            className="flex-1 min-w-[200px] border border-[var(--border)] bg-white px-2 py-1.5"
          />
          <select
            data-testid="review-link-expiry"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
            className="border border-[var(--border)] bg-white px-2 py-1.5"
            aria-label={tr("Link expiry")}
          >
            {EXPIRY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-testid="review-link-create"
            onClick={() => create.mutate()}
            disabled={create.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--slate-900)] text-white hover:opacity-90 disabled:opacity-50"
          >
            {create.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Plus className="w-3.5 h-3.5" />
            )}{tr("Create review link")}</button>
        </div>

        {fresh && (
          <div
            data-testid="review-link-fresh"
            className="border border-[var(--accent)] bg-white p-3 text-xs space-y-2"
          >
            <div className="text-[10px] uppercase tracking-wider text-[var(--accent)] font-mono-display">{tr("New link — copy it now, it will not be shown again")}</div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={fresh.url}
                onFocus={(e) => e.target.select()}
                className="flex-1 border border-[var(--border)] bg-[var(--secondary)] px-2 py-1.5 font-mono-display text-[11px]"
              />
              <button
                type="button"
                data-testid="review-link-copy"
                onClick={() => copy(fresh.url)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-[var(--border)] hover:border-[var(--ink)]"
              >
                <Copy className="w-3.5 h-3.5" />{" "}{tr("Copy")}</button>
              <a
                href={fresh.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[var(--ink-muted)] hover:text-[var(--ink)]"
                aria-label={tr("Open the review page")}
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        )}

        {linksQ.isLoading ? null : links.length === 0 ? (
          <div className="text-[11px] text-[var(--ink-muted)]">{tr("No review links yet.")}</div>
        ) : (
          <ul className="space-y-1.5">
            {[...active, ...inactive].map((l) => {
              const isActive = l.status === "active";
              const busy = revoke.isPending && revoke.variables === l.id;
              return (
                <li
                  key={l.id}
                  data-testid={`review-link-${l.id}`}
                  className={
                    "flex items-center gap-3 border border-[var(--border)] px-3 py-2 text-xs" +
                    (isActive ? "" : " opacity-60")
                  }
                >
                  <Link2 className="w-3.5 h-3.5 text-[var(--ink-muted)] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate font-medium">
                      {l.label || tr("Untitled link")}
                      <span className="ml-2 font-mono-display text-[10px] text-[var(--ink-muted)]">
                        {l.token_prefix}…
                      </span>
                    </div>
                    <div className="text-[10px] text-[var(--ink-muted)] font-mono-display truncate">{tr("Created {date}{value} · {value2} · {value3} · {comment_count} comment{s}", { date: new Date(l.created_at).toLocaleDateString(uiLocale()), value: l.created_by_email ? tr(" by {created_by_email}", { created_by_email: l.created_by_email }) : "", value2: l.expires_at
                        ? tr("expires {date}", { date: new Date(l.expires_at).toLocaleDateString(uiLocale()) })
                        : tr("never expires"), value3: l.view_count
                        ? tr("opened {view_count}× (last {date})", { view_count: l.view_count, date: new Date(l.last_viewed_at).toLocaleDateString(uiLocale()) })
                        : tr("not opened yet"), comment_count: l.comment_count, s: l.comment_count === 1 ? "" : "s" })}
                      {!isActive && (
                        <span className="ml-2 uppercase tracking-wider text-red-600">
                          {l.status === "revoked" ? tr("withdrawn") : l.status}
                        </span>
                      )}
                    </div>
                  </div>
                  {isActive && (
                    <button
                      type="button"
                      data-testid={`review-link-revoke-${l.id}`}
                      onClick={() => {
                        if (
                          window.confirm(
                            tr("Withdraw this link? Anyone who has it will see 'This review link is no longer valid'. Comments already left stay here."),
                          )
                        ) {
                          revoke.mutate(l.id);
                        }
                      }}
                      disabled={busy}
                      className="inline-flex items-center gap-1 text-[var(--ink-muted)] hover:text-red-600 disabled:opacity-50"
                    >
                      {busy ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Ban className="w-3.5 h-3.5" />
                      )}{tr("Withdraw")}</button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}